import { LegalProse } from './legal-prose';
import { useLegalContent, type TLegalContentType } from './use-legal-content';

import { Spinner } from '@/components/ui/spinner';

interface LegalDocumentPageProps {
    title: string;
    /** Document version from the registry, e.g. "2026.07". */
    version: string;
    /** ISO effective date from the registry. */
    effectiveDate: string;
    contentType: TLegalContentType;
    locale: string;
}

/**
 * Shared shell for the static legal document pages (Legal Notice, Privacy
 * Policy): page header with the mandated version identifier and effective
 * date (ESCLOUD-78), followed by the pre-rendered document body.
 */
export function LegalDocumentPage({
    title,
    version,
    effectiveDate,
    contentType,
    locale,
}: LegalDocumentPageProps) {
    const { data: html, isPending, isError } = useLegalContent(contentType, locale);

    return (
        <article className="mx-auto w-full max-w-3xl py-6">
            <header className="mb-4 border-b pb-4">
                <h1 className="text-2xl font-semibold">{title}</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                    Version {version} — effective {effectiveDate}
                </p>
            </header>
            {isPending && (
                <div className="flex justify-center py-12">
                    <Spinner />
                </div>
            )}
            {isError && (
                <p className="py-12 text-center text-sm text-destructive">
                    This document could not be loaded. Please try again or contact contact@therenva.com.
                </p>
            )}
            {html !== undefined && <LegalProse html={html} />}
        </article>
    );
}
